import {
  INSTALLABLE_MODULE_KEYS,
  isInstallableModuleKey,
  isInstalledModuleAvailable,
  moduleUnavailablePhrase,
  type InstallableModuleKey,
  type ModuleAvailabilityStatus,
} from "@playbookai/types";
import {
  MODULE_DISPLAY_NAMES,
  type ModuleAvailabilitySource,
  type ModuleStatusMap,
} from "@playbookai/api-clients";
import { getRuntimeConfig } from "../config/runtime.js";

/**
 * May this workspace use an installable module (ADR-0100)? Installed by the
 * deployment (`installedModules`) AND in the tenant's available set (the
 * session claim, in Auth keys — `isInstalledModuleAvailable` translates `kpi`
 * to `kpi_manager`). Unlike `getEnabledModules` it can answer for KPI, which
 * has no shell `ModuleId`.
 *
 * Before a claim exists, only "installed" decides — the same as
 * `getEnabledModules` with no tenant list.
 *
 * Lives apart from the module registry (which re-exports it) so the auth store
 * can publish availability without loading every feature manifest.
 */
export function isModuleAvailable(
  key: InstallableModuleKey,
  tenantEnabledModules?: readonly string[],
): boolean {
  const { installedModules } = getRuntimeConfig();
  if (tenantEnabledModules === undefined) {
    return installedModules.includes(key);
  }
  return isInstalledModuleAvailable(key, {
    installed: installedModules,
    availableAuthKeys: tenantEnabledModules,
  });
}

/**
 * The same rule as a status, for the "turned off" / "isn't available" wording
 * (d7): not installed wins, then `isModuleAvailable` decides.
 */
export function moduleStatus(
  key: InstallableModuleKey,
  tenantEnabledModules?: readonly string[],
): ModuleAvailabilityStatus {
  if (!getRuntimeConfig().installedModules.includes(key)) {
    return "not_installed";
  }
  return isModuleAvailable(key, tenantEnabledModules)
    ? "available"
    : "turned_off";
}

/** The installable modules this workspace may use, as a set. */
export function availableModuleSet(
  tenantEnabledModules?: readonly string[],
): ReadonlySet<InstallableModuleKey> {
  return new Set(
    INSTALLABLE_MODULE_KEYS.filter((key) =>
      isModuleAvailable(key, tenantEnabledModules),
    ),
  );
}

export function moduleStatusMap(
  tenantEnabledModules?: readonly string[],
): ModuleStatusMap {
  return Object.freeze(
    Object.fromEntries(
      INSTALLABLE_MODULE_KEYS.map((key) => [
        key,
        moduleStatus(key, tenantEnabledModules),
      ]),
    ) as Record<InstallableModuleKey, ModuleAvailabilityStatus>,
  );
}

const listeners = new Set<() => void>();
let snapshot: ModuleStatusMap | null = null;

/**
 * The reactive seam features read through `useModuleAvailability` (ADR-0100).
 * The auth store publishes it whenever the session's claims change, so a
 * session refresh — the keep-alive's, or the one a `module_unavailable`
 * refusal starts — reaches every open screen.
 */
export const moduleAvailabilitySource: ModuleAvailabilitySource = Object.freeze(
  {
    getSnapshot: () => (snapshot ??= moduleStatusMap()),
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  },
);

/** Publish only when a status moved, so an unchanged /me wakes nobody. */
export function publishModuleAvailability(
  tenantEnabledModules: readonly string[] | undefined,
): void {
  const next = moduleStatusMap(tenantEnabledModules);
  if (
    snapshot !== null &&
    INSTALLABLE_MODULE_KEYS.every((key) => snapshot?.[key] === next[key])
  ) {
    return;
  }
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

/**
 * The sentence for "you asked to open a module this shell has not mounted"
 * (an Open button or a search result reached a module that is off). Said
 * plainly instead of the click doing nothing. A module the shell has no
 * manifest for while the rule calls it available can only be a stale claim,
 * so it reads as turned off.
 */
export function moduleUnavailableNotice(
  moduleId: string,
  label: string,
  tenantEnabledModules?: readonly string[],
): string {
  if (!isInstallableModuleKey(moduleId)) {
    return moduleUnavailablePhrase(label, "not_installed");
  }
  const status = moduleStatus(moduleId, tenantEnabledModules);
  return moduleUnavailablePhrase(
    MODULE_DISPLAY_NAMES[moduleId],
    status === "available" ? "turned_off" : status,
  );
}

export function __resetModuleAvailabilityForTests(): void {
  snapshot = null;
  listeners.clear();
}
